import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Voucher, VoucherCreateDTO } from './voucher.model';
import { toUTCSmallDate } from '../dates.service';
import * as moment from 'moment';
import * as numeral from 'numeral';
import { VOUCHER_COMPUTE_MODE } from './vouchers.constants';
import { OrderResult } from '../orders/order.models';
import * as _ from 'lodash';

@Injectable()
export class VoutchersService {
    API_URL = environment.apiUrl;

    constructor(
        private http: HttpClient,
    ) {}

    get(code: string) {
        return this.http.get<Voucher>(`${this.API_URL}/vouchers/${encodeURIComponent(code)}`);
    }

    list() {
        return this.http.get<Voucher[]>(`${this.API_URL}/vouchers`);
    }

    create(data: VoucherCreateDTO) {
        return this.http.post<Voucher>(`${this.API_URL}/vouchers`, data);
    }

    update(data: VoucherCreateDTO) {
        return this.http.put<Voucher>(`${this.API_URL}/vouchers`, data);
    }

    isValid(voucher: Voucher) {
        const date = toUTCSmallDate();
        return !voucher.disabled
          && !voucher.usedAt
          && moment(date).isSameOrAfter(voucher.start)
          && moment(date).isSameOrBefore(voucher.expiration);
    }

    isValidProductIds(voucher: Voucher, productIds: string[]) {
      if (!voucher.products || !voucher.products.length) {
        return true;
      }
      for (const id of productIds) {
        if (voucher.products.indexOf(id) >= 0) {
          return true;
        }
      }
      return false;
    }

    getTotalRemise(amount: number, voucher: Voucher) {
        if (!voucher || amount <= 0) return 0;
        const remise = voucher.method === VOUCHER_COMPUTE_MODE.PERCENT
            ? numeral(amount).multiply(this.realValue(voucher)).value()
            : this.realValue(voucher);
        return Number.parseFloat(numeral(Math.min(amount, remise)).format('0.00'));
    }

    computeTotal(amount: number, voucher: Voucher) {
        if (!voucher) {
            return amount;
        }
        const remise = this.getTotalRemise(amount, voucher);
        return Number.parseFloat(
            numeral(Math.max(numeral(amount).subtract(remise).value(), 0)).format('0.00')
        );
    }

    realValue(voucher: Voucher) {
        return voucher.method === VOUCHER_COMPUTE_MODE.PERCENT
        ? numeral(voucher.value).divide(100).value()
        : numeral(voucher.value).value();
    }

    getDiscountRate(amountTTC: number, voucher: Voucher) {
        if (!voucher || amountTTC <= 0) return 0;
        if (voucher.method === VOUCHER_COMPUTE_MODE.PERCENT) {
            return numeral(voucher.value).divide(100).value();
        } else {
            const remise = this.getTotalRemise(amountTTC, voucher);
            return numeral(remise).divide(amountTTC).value();
        }
    }

    computeAmountTTC(order: OrderResult) {
        if (order.voucher) {
            return this.computeTotal(order.amountTotal, order.voucher);
        }
        return order.amountTotal;
    }

    computeAmountHT(order: OrderResult) {
        if (!order) return;
        const amountTTC = this.computeAmountTTC(order);
        return numeral(amountTTC).subtract(this.computeAmountVAT(order)).value();
    }

    protected computeItemTotalVAT(vatRate: number, amountTTC: number, discountRate = 0) {
        const d = numeral(100).add(vatRate).value();
        const v = numeral(amountTTC).divide(d);
        const val = v.multiply(vatRate);
        if (discountRate > 0)
            return Number.parseFloat(val.divide(1 + discountRate).format("0.00"));
        return Number.parseFloat(val.format("0.00"));
    }

    computeAmountVAT(order: OrderResult) {
        if (!order) return;
        const amountTTC = this.computeAmountTTC(order);
        if (!order.amountTotal || amountTTC <= 0) {
            return 0;
        }

        const ratio = numeral(amountTTC).divide(order.amountTotal).value();
        return Number.parseFloat(
            numeral(order.amountVAT).multiply(ratio).format('0.00')
        );
    }
}



// WEBPACK FOOTER //
// /usr/app/src/app/core/services/vouchers/vouchers.service.ts